import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiEdit, FiTrash2, FiPlus, FiSearch, FiMapPin, FiPhone, FiMail, FiAward, FiUsers } from 'react-icons/fi';
import api from '../services/api';
import { toast } from 'react-toastify';
import { confirmDelete } from '../utils/swal';

const TrainerList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [trainers, setTrainers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const [filters, setFilters] = useState({
    search: '',
    branch_id: searchParams.get('branch_id') || '',
    is_active: 'all',
    specialization: ''
  });

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchTrainers();
  }, [filters, pagination.page]);

  const fetchBranches = async () => {
    try {
      const response = await api.get('/branches?is_active=true');
      setBranches(response.data.data || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchTrainers = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (filters.search) params.search = filters.search;
      if (filters.branch_id) params.branch_id = filters.branch_id;
      if (filters.is_active !== 'all') params.is_active = filters.is_active === 'active';
      if (filters.specialization) params.specialization = filters.specialization;

      const response = await api.get('/trainers', { params });
      const data = response.data.data;

      setTrainers(data.trainers || []);
      setPagination(prev => ({
        ...prev,
        total: data.pagination.total,
        totalPages: data.pagination.totalPages
      }));
    } catch (error) {
      console.error('Error fetching trainers:', error);
      toast.error('Failed to load trainers');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (trainer) => {
    const trainerName = `${trainer.first_name} ${trainer.last_name}`;
    const confirmed = await confirmDelete(trainerName, 'trainer');
    if (!confirmed) return;

    try {
      await api.delete(`/trainers/${trainer.id}`);
      toast.success('Trainer deleted successfully');
      fetchTrainers();
    } catch (error) {
      console.error('Error deleting trainer:', error);
      toast.error(error.response?.data?.message || 'Failed to delete trainer');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchTrainers();
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  if (loading && trainers.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Trainers</h1>
          <p className="text-gray-600">Manage gym trainers and staff</p>
        </div>
        <button
          onClick={() => navigate('/trainers/new')}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <FiPlus /> Add Trainer
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                placeholder="Search by name, ID, phone..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Branch Filter */}
          <div>
            <select
              value={filters.branch_id}
              onChange={(e) => handleFilterChange('branch_id', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Branches</option>
              {branches.map(branch => (
                <option key={branch.id} value={branch.id}>{branch.name}</option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={filters.is_active}
              onChange={(e) => handleFilterChange('is_active', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </form>
      </div>

      {/* Trainers List */}
      {trainers.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500 text-lg">No trainers found</p>
          <button
            onClick={() => navigate('/trainers/new')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            Add your first trainer
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {trainers.map((trainer) => (
              <div
                key={trainer.id}
                className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow overflow-hidden"
              >
                {/* Trainer Photo & Header */}
                <div className="relative h-32 bg-gradient-to-r from-blue-500 to-blue-600">
                  {trainer.photo ? (
                    <img
                      src={trainer.photo}
                      alt={`${trainer.first_name} ${trainer.last_name}`}
                      className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-24 h-24 rounded-full border-4 border-white object-cover"
                    />
                  ) : (
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-24 h-24 rounded-full border-4 border-white bg-gray-300 flex items-center justify-center">
                      <span className="text-3xl font-bold text-gray-600">
                        {trainer.first_name?.charAt(0)}{trainer.last_name?.charAt(0)}
                      </span>
                    </div>
                  )}
                  <span
                    className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold ${
                      trainer.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {trainer.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {/* Card Body */}
                <div className="pt-14 p-6">
                  {/* Name & ID */}
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800 mb-1">
                      {trainer.first_name} {trainer.last_name}
                    </h3>
                    <p className="text-sm text-gray-500 font-mono">ID: {trainer.employee_id}</p>
                  </div>

                  {/* Info */}
                  <div className="space-y-2 mb-4">
                    {/* Branch */}
                    {trainer.branch_name && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <FiMapPin className="flex-shrink-0 text-gray-400" />
                        <span>{trainer.branch_name}</span>
                      </div>
                    )}

                    {/* Phone */}
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FiPhone className="flex-shrink-0 text-gray-400" />
                      <span>{trainer.phone}</span>
                    </div>

                    {/* Email */}
                    {trainer.email && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <FiMail className="flex-shrink-0 text-gray-400" />
                        <span className="truncate">{trainer.email}</span>
                      </div>
                    )}

                    {/* Specialization */}
                    {trainer.specialization && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <FiAward className="flex-shrink-0 text-gray-400" />
                        <span>{trainer.specialization}</span>
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                    <div className="flex items-center gap-2 text-sm">
                      <FiUsers className="text-blue-600" />
                      <span className="font-semibold">{trainer.assigned_members || 0}</span>
                      <span className="text-gray-500">Members</span>
                    </div>
                    {trainer.experience_years && (
                      <div className="text-sm text-gray-600">
                        <span className="font-semibold">{trainer.experience_years}</span> yrs exp
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                  <button
                    onClick={() => navigate(`/trainers/${trainer.id}`)}
                    className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                  >
                    View Details
                  </button>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/trainers/edit/${trainer.id}`)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <FiEdit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(trainer)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <FiTrash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mt-6 flex justify-center gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-gray-600">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= pagination.totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TrainerList;
